import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  gestifyApiRow,
  type ImportAction,
  type ImportPreview,
  type ImportResultResponse,
} from '@k-management/shared';
import { type ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { GestifyConnectDialog } from '../../components/GestifyConnectDialog.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { useGestifyBrowser } from '../../lib/gestify-browser.js';
import { registryKeys } from './registry-ui.js';

const ACTION_LABELS: Record<ImportAction, string> = {
  new: 'Da importare',
  existing: 'Già in anagrafica',
  duplicate: 'Doppione nel file',
  'shared-vat': 'Stessa partita IVA',
  error: 'Non importabile',
};

const ACTION_PILLS: Record<ImportAction, string> = {
  new: 'pill ok',
  existing: 'pill muted',
  duplicate: 'pill warn',
  'shared-vat': 'pill warn',
  error: 'pill muted',
};

/**
 * Import of the registry export of Gestify (proposal §2.7): first the preview
 * of what would happen row by row, then only the rows confirmed here.
 */
export function ImportPage() {
  usePageTitle('Importa anagrafiche');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fileName, setFileName] = useState<string | null>(null);
  // What the preview was made of: the export file, or the rows read from Gestify.
  const [source, setSource] = useState<
    { file: string } | { rows: Record<string, string | null>[] } | null
  >(null);
  const gestify = useGestifyBrowser();
  const [connecting, setConnecting] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<ImportResultResponse | null>(null);

  const read = useMutation({
    mutationFn: (body: { file: string } | { rows: Record<string, string | null>[] }) =>
      apiRequest<ImportPreview>('/subjects/import/preview', { method: 'POST', body }),
    onSuccess: (data) => {
      setPreview(data);
      setResult(null);
      setSelected(new Set(data.rows.filter((row) => row.selected).map((row) => row.line)));
    },
  });
  const run = useMutation({
    mutationFn: () =>
      apiRequest<ImportResultResponse>('/subjects/import', {
        method: 'POST',
        body: { ...source, lines: [...selected] },
      }),
    onSuccess: async (data) => {
      setResult(data);
      setPreview(null);
      await queryClient.invalidateQueries({ queryKey: registryKeys.all });
    },
  });

  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    setFileName(chosen.name);
    const buffer = await chosen.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let index = 0; index < bytes.length; index += 8192) {
      binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
    }
    const base64 = btoa(binary);
    setSource({ file: base64 });
    read.mutate({ file: base64 });
  };

  /** The registry of Gestify read by this browser (ADR-017), as rows of the export file. */
  const fromGestify = useMutation({
    mutationFn: async () => {
      const records = await gestify.readAll('/api/anagrafica/v1/list', (done, total) =>
        setProgress(`Lette ${done}${total !== null ? ` di ${total}` : ''} anagrafiche…`),
      );
      return records.map(gestifyApiRow);
    },
    onSuccess: (rows) => {
      setProgress(null);
      setFileName(`Gestify, ${rows.length} anagrafiche`);
      setSource({ rows });
      read.mutate({ rows });
    },
    onError: () => setProgress(null),
  });

  const commands: (Command | false)[] = [
    Boolean(preview) && {
      key: 'import',
      label: `Importa (${selected.size})`,
      icon: 'plus',
      primary: true,
      disabled: selected.size === 0 || run.isPending,
      onClick: () => run.mutate(),
    },
    { key: 'back', label: 'Anagrafica', icon: 'users', onClick: () => navigate('/anagrafica') },
  ];
  useCommands(commands.filter((command): command is Command => Boolean(command)));

  const toggle = (line: number) => {
    const next = new Set(selected);
    if (next.has(line)) next.delete(line);
    else next.add(line);
    setSelected(next);
  };

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Importa anagrafiche</h1>
          <p className="muted">
            Da Gestify, direttamente o dal suo export (Excel o CSV): prima vedi cosa succederebbe
            riga per riga, poi importi solo quelle che confermi. Ripetibile: chi è già in anagrafica
            viene saltato.
          </p>
        </div>
      </div>

      <div className="card stack">
        <div className="row between">
          <div>
            <strong>Direttamente da Gestify</strong>
            <small className="muted block">
              Il browser legge le anagrafiche con il tuo utente Gestify (ADR-017).
              {gestify.connectedAs && ` Collegato come ${gestify.connectedAs}.`}
            </small>
          </div>
          <button
            type="button"
            className="primary"
            disabled={fromGestify.isPending || read.isPending}
            onClick={() => (gestify.connectedAs ? fromGestify.mutate() : setConnecting(true))}
          >
            {gestify.connectedAs ? 'Leggi da Gestify' : 'Collegati a Gestify'}
          </button>
        </div>
        {progress && <p className="muted">{progress}</p>}
        {fromGestify.error && <p className="error">{fromGestify.error.message}</p>}
        <label className="field">
          Oppure il file dell’export (Excel o CSV)
          <input type="file" accept=".xlsx,.xls,.csv" onChange={(event) => void choose(event)} />
        </label>
        {fileName && <p className="muted small">{fileName}</p>}
        {read.isPending && <p className="muted">Lettura…</p>}
        {read.error && <p className="error">{read.error.message}</p>}
      </div>
      {connecting && (
        <GestifyConnectDialog
          onClose={() => setConnecting(false)}
          onConnected={() => fromGestify.mutate()}
        />
      )}

      {result && (
        <div className="card stack">
          <h2>Import eseguito</h2>
          <p>
            {result.created === 1 ? '1 soggetto creato' : `${result.created} soggetti creati`}
            {result.codes.length > 0 &&
              `, con i codici ${result.codes.slice(0, 6).join(', ')}${result.codes.length > 6 ? '…' : ''}`}
            .
          </p>
          {result.errors.length > 0 && (
            <>
              <p className="muted">Righe non importate:</p>
              <ul className="plain-list">
                {result.errors.map((error) => (
                  <li key={error.line}>
                    riga {error.line}: {error.message}
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="row">
            <button type="button" className="primary" onClick={() => navigate('/anagrafica')}>
              Vai all’anagrafica
            </button>
          </div>
        </div>
      )}

      {preview && (
        <>
          <div className="chips">
            {(Object.keys(ACTION_LABELS) as ImportAction[]).map((action) => (
              <span key={action} className={ACTION_PILLS[action]}>
                {ACTION_LABELS[action]}: {preview.summary[action]}
              </span>
            ))}
          </div>
          <p className="muted small">
            Le righe con la stessa partita IVA (gruppi IVA o doppioni del gestionale) restano da
            scegliere: spunta quella da tenere. Le altre si possono importare più tardi, cambiando
            l’identificativo.
          </p>
          <div className="table-wrap card flush">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="grid-check">Importa</th>
                  <th>Riga</th>
                  <th>Ragione sociale</th>
                  <th>Ruoli</th>
                  <th>Identificativo</th>
                  <th>Comune</th>
                  <th>Esito</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.line} className={row.action === 'error' ? 'inactive' : undefined}>
                    <td className="grid-check">
                      <input
                        type="checkbox"
                        aria-label={`Importa la riga ${row.line}`}
                        checked={selected.has(row.line)}
                        disabled={row.action === 'error' || row.action === 'existing'}
                        onChange={() => toggle(row.line)}
                      />
                    </td>
                    <td>{row.line}</td>
                    <td>
                      <strong>{row.subject.name}</strong>
                    </td>
                    <td>{row.subject.roles.join(', ')}</td>
                    <td className="code">{row.subject.vatNumber ?? row.subject.taxCode}</td>
                    <td>{row.subject.city}</td>
                    <td>
                      <span className={ACTION_PILLS[row.action]}>{ACTION_LABELS[row.action]}</span>
                    </td>
                    <td className="details">
                      {row.reason && <span>{row.reason}</span>}
                      {row.issues.map((issue) => (
                        <span key={issue} className="muted">
                          {issue}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {run.error && <p className="error">{run.error.message}</p>}
          <div className="actions">
            <button
              type="button"
              className="primary"
              disabled={selected.size === 0 || run.isPending}
              onClick={() => run.mutate()}
            >
              {run.isPending ? 'Import in corso…' : `Importa ${selected.size} righe`}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
